/**
 * What the question catalogue is built from, apart from the codebook: the
 * parsed questionnaire and the routing. The parse is quick and the screen
 * waits for it; the routing takes seconds and arrives later. Until it does,
 * `routing` is null and the catalogue is built without it; once it arrives
 * the catalogue is built again. A routing query that fails (no auth token,
 * a refused request) reads as "unavailable", as on a server without the
 * base check: the tables then use the "answered" base, and say why.
 */
import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { queryKeys } from "api/queryKeys"
import { useQuestionnaire } from "features/QuestionnaireEditor/api/useQuestionnaire"
import type { CheckQuestionnaireResult } from "types/questionnaire.types"
import { variantsOf } from "../model/catalogue"
import type { RoutingResult } from "./routingProvider"
import { useRouting } from "./useRouting"

export interface CatalogueSources {
  /** The check's `result` (`{ parse, notifications }`); null until it is in. */
  parse: unknown
  variants: string[]
  /** null while the routing loads. */
  routing: RoutingResult | null
  /** The questionnaire is still loading. */
  isLoading: boolean
  /**
   * The routing is being read: the first time, or again as the screen opens
   * (the last routing is used meanwhile). It never holds the screen; it
   * holds the tab book.
   */
  routingLoading: boolean
  error: string | null
}

/** What a failed routing query reads as: the words of `routingProvider`'s "unavailable". */
export const ROUTING_UNAVAILABLE: RoutingResult = {
  ok: false,
  reason: "unavailable",
  message: "Base from routing is not available on this server.",
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v)

/**
 * The check's `result`. The Conductor can refuse with HTTP 200 and a body of
 * `{"Err msg": ...}`: that is a failure too, never an empty questionnaire.
 */
const checkResult = (body: unknown): CheckQuestionnaireResult => {
  if (isRecord(body) && isRecord(body.result))
    return body.result as unknown as CheckQuestionnaireResult
  const refused = isRecord(body) ? body["Err msg"] : undefined
  throw new Error(
    typeof refused === "string" && refused.trim()
      ? refused
      : "The questionnaire check gave no result."
  )
}

/**
 * The routing, unless the server walked another variant than the catalogue
 * is built from (`variant`, or the first variant of the questionnaire when
 * none is chosen): that routing is not used, as on a server without it. A
 * report that names no variant cannot be checked and is used.
 */
const routingFor = (
  routing: RoutingResult | undefined,
  wanted: string | null
): RoutingResult | undefined =>
  routing?.ok && routing.info.variant && wanted !== null && routing.info.variant !== wanted
    ? ROUTING_UNAVAILABLE
    : routing

/** What a project without a questionnaire has: nothing to read, nothing on its way. */
const NOTHING: CatalogueSources = {
  parse: null,
  variants: [],
  routing: null,
  isLoading: false,
  routingLoading: false,
  error: null,
}

/**
 * `enabled` false (a project without a questionnaire): neither the
 * questionnaire nor the routing is asked for, and the sources are empty.
 * `withRouting` false (a caller that needs the questions only): the routing,
 * a walk of the data on the server, is never asked for; `routing` stays null
 * and `routingLoading` false.
 */
export const useCatalogueSources = (
  projectId: string,
  owner: string,
  variant: string | null,
  enabled = true,
  withRouting = true
): CatalogueSources => {
  const { checkQuestionnaireByID } = useQuestionnaire()
  const parse = useQuery({
    queryKey: [...queryKeys.tabSpecs.root, projectId, "parse"],
    queryFn: async () => checkResult((await checkQuestionnaireByID(projectId)).data),
    enabled: !!projectId && enabled,
    staleTime: 60_000,
    // Read again every time the screen opens: it may have been edited since.
    refetchOnMount: "always",
    retry: false,
    meta: { errorTitle: "Could not read the questionnaire" },
  })
  const routing = useRouting(projectId, owner, variant, enabled && withRouting && !!projectId)
  const data = parse.data ?? null
  const variants = useMemo(() => variantsOf(data), [data])
  const checked = routingFor(routing.data, variant ?? variants[0] ?? null)
  if (!enabled) return NOTHING
  return {
    parse: data,
    variants,
    // None asked for: none given, not even one another screen left in the cache.
    routing: !withRouting ? null : (checked ?? (routing.isError ? ROUTING_UNAVAILABLE : null)),
    // Routing is optional and takes seconds: it never holds the screen.
    isLoading: parse.isLoading,
    routingLoading: withRouting && routing.isFetching,
    error: parse.error ? "The questionnaire could not be read." : null,
  }
}
